import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import KPICard from './components/KPICard';
import TeamMemberCard from './components/TeamMemberCard';
import PerformanceChart from './components/PerformanceChart';
import ApprovalQueueItem from './components/ApprovalQueueItem';
import SystemHealthPanel from './components/SystemHealthPanel';
import AlertNotification from './components/AlertNotification';
import QuickActionPanel from './components/QuickActionPanel';
import Header from '../../components/Header';

const SupervisorDashboardAndAnalytics = () => {
  const navigate = useNavigate();
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const kpiData = [
  {
    id: 1,
    title: "Bearbeitete Klienten heute",
    value: "247",
    change: "+12.5%",
    changeType: "positive",
    icon: "Users",
    iconColor: "bg-primary",
    trend: true
  },
  {
    id: 2,
    title: "Team-Produktivität",
    value: "94.2%",
    change: "+3.8%",
    changeType: "positive",
    icon: "TrendingUp",
    iconColor: "bg-success",
    trend: true
  },
  {
    id: 3,
    title: "Systemauslastung",
    value: "78%",
    change: "-5.2%",
    changeType: "positive",
    icon: "Activity",
    iconColor: "bg-warning",
    trend: true
  },
  {
    id: 4,
    title: "Ausstehende Genehmigungen",
    value: "23",
    change: "+8",
    changeType: "negative",
    icon: "Clock",
    iconColor: "bg-error",
    trend: false
  }];


  const teamMembers = [
  {
    id: 1,
    name: "Anna Schmidt",
    role: "Sachbearbeiter",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png",
    avatarAlt: "Professional headshot of woman with blonde hair in business attire smiling at camera",
    status: "Aktiv",
    activeClients: 12,
    hoursToday: 6.5,
    workloadPercentage: 85
  },
  {
    id: 2,
    name: "Michael Weber",
    role: "Senior Berater",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab0dfeed-1763294178564.png",
    avatarAlt: "Professional headshot of man with dark hair wearing navy suit and tie",
    status: "Aktiv",
    activeClients: 15,
    hoursToday: 7.2,
    workloadPercentage: 92
  },
  {
    id: 3,
    name: "Sarah Müller",
    role: "Sachbearbeiter",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17badaf26-1763295515136.png",
    avatarAlt: "Professional headshot of woman with brown hair in white blouse smiling warmly",
    status: "Pause",
    activeClients: 8,
    hoursToday: 5.0,
    workloadPercentage: 65
  },
  {
    id: 4,
    name: "Thomas Fischer",
    role: "Team Lead",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd29f585-1763300949045.png",
    avatarAlt: "Professional headshot of man with glasses wearing gray suit jacket",
    status: "Aktiv",
    activeClients: 10,
    hoursToday: 6.8,
    workloadPercentage: 78
  },
  {
    id: 5,
    name: "Julia Becker",
    role: "Sachbearbeiter",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19b7f77ca-1763293657165.png",
    avatarAlt: "Professional headshot of woman with red hair in black blazer with confident expression",
    status: "Offline",
    activeClients: 0,
    hoursToday: 0,
    workloadPercentage: 0
  }];


  const performanceData = [
  { name: 'Mo', bearbeitungen: 245, genehmigungen: 189 },
  { name: 'Di', bearbeitungen: 268, genehmigungen: 201 },
  { name: 'Mi', bearbeitungen: 289, genehmigungen: 215 },
  { name: 'Do', bearbeitungen: 312, genehmigungen: 234 },
  { name: 'Fr', bearbeitungen: 298, genehmigungen: 221 },
  { name: 'Sa', bearbeitungen: 156, genehmigungen: 112 },
  { name: 'So', bearbeitungen: 134, genehmigungen: 98 }];


  const productivityData = [
  { name: 'KW 1', produktivität: 88 },
  { name: 'KW 2', produktivität: 91 },
  { name: 'KW 3', produktivität: 89 },
  { name: 'KW 4', produktivität: 94 }];


  const approvalQueue = [
  {
    id: 1,
    type: "Datenzugriff",
    title: "Zugriff auf vertrauliche Klientendaten",
    description: "Anfrage für erweiterten Datenzugriff zur Bearbeitung eines komplexen Falls",
    requestedBy: "Anna Schmidt",
    timeAgo: "vor 15 Min.",
    priority: "Hoch"
  },
  {
    id: 2,
    type: "Massenaktion",
    title: "Massenaktualisierung von 45 Klientenprofilen",
    description: "Aktualisierung der Kontaktinformationen nach Systemumstellung",
    requestedBy: "Michael Weber",
    timeAgo: "vor 1 Std.",
    priority: "Mittel"
  },
  {
    id: 3,
    type: "Systemänderung",
    title: "Änderung der Workflow-Einstellungen",
    description: "Anpassung der automatischen Benachrichtigungsregeln für das Team",
    requestedBy: "Thomas Fischer",
    timeAgo: "vor 2 Std.",
    priority: "Niedrig"
  },
  {
    id: 4,
    type: "Datenlöschung",
    title: "Löschung abgelaufener Klientendaten",
    description: "DSGVO-konforme Löschung von 12 Klientenprofilen nach Ablauf der Aufbewahrungsfrist",
    requestedBy: "Sarah Müller",
    timeAgo: "vor 3 Std.",
    priority: "Hoch"
  }];


  const systemHealth = [
  {
    id: 1,
    name: "Hauptdatenbank",
    icon: "Database",
    status: "Aktiv",
    lastSync: "vor 2 Min.",
    uptime: "99.9%"
  },
  {
    id: 2,
    name: "LDAP-Authentifizierung",
    icon: "Lock",
    status: "Aktiv",
    lastSync: "vor 5 Min.",
    uptime: "100%"
  },
  {
    id: 3,
    name: "Sozialversicherung API",
    icon: "Link",
    status: "Warnung",
    lastSync: "vor 45 Min.",
    uptime: "98.2%"
  },
  {
    id: 4,
    name: "Backup-System",
    icon: "HardDrive",
    status: "Aktiv",
    lastSync: "vor 1 Std.",
    uptime: "99.5%"
  }];


  const alerts = [
  {
    id: 1,
    severity: "Kritisch",
    title: "Systemfehler erkannt",
    message: "Verbindungsfehler zur Sozialversicherung API. Manuelle Dateneingabe erforderlich.",
    time: "vor 10 Min."
  },
  {
    id: 2,
    severity: "Warnung",
    title: "Hohe Systemauslastung",
    message: "Datenbankauslastung bei 85%. Überwachung empfohlen.",
    time: "vor 25 Min."
  }];


  const timeRangeOptions = [
  { value: 'today', label: 'Heute' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'quarter', label: 'Dieses Quartal' },
  { value: 'year', label: 'Dieses Jahr' }];


  const departmentOptions = [
  { value: 'all', label: 'Alle Abteilungen' },
  { value: 'client-services', label: 'Klientendienste' },
  { value: 'job-placement', label: 'Arbeitsvermittlung' },
  { value: 'admin', label: 'Verwaltung' }];


  useEffect(() => {
    setActiveAlerts(alerts);
  }, []);

  const handleViewMemberDetails = (member) => {
    console.log('Viewing details for:', member?.name);
  };

  const handleApprove = (item) => {
    console.log('Approved:', item?.title);
  };

  const handleReject = (item) => {
    console.log('Rejected:', item?.title);
  };

  const handleViewDetails = (item) => {
    console.log('Viewing details:', item?.title);
  };

  const handleDismissAlert = (alert) => {
    setActiveAlerts((prev) => prev?.filter((a) => a?.id !== alert?.id));
  };

  const handleInvestigateAlert = (alert) => {
    console.log('Investigating alert:', alert?.title);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
  };

  const handleExportReport = () => {
    console.log('Exporting comprehensive report...');
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <Icon name="BarChart3" size={24} color="white" />
            </div>
            <div className="sidebar-brand">
              <span className="text-base font-semibold text-foreground">Bundesagentur</span>
              <span className="text-xs text-muted-foreground">für Arbeit</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-section">
              <div className="sidebar-section-title">Hauptmenü</div>
              <div className="sidebar-item" onClick={() => navigate('/case-worker-dashboard')}>
                <Icon name="LayoutDashboard" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Dashboard</span>
              </div>
              <div className="sidebar-item" onClick={() => navigate('/client-search-and-directory')}>
                <Icon name="Search" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Klientensuche</span>
              </div>
              <div className="sidebar-item" onClick={() => navigate('/unique-id-generation-system')}>
                <Icon name="Hash" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">ID-Generierung</span>
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-section-title">Verwaltung</div>
              <div className="sidebar-item" onClick={() => navigate('/bulk-operations-center')}>
                <Icon name="Layers" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Massenoperationen</span>
              </div>
              <div className="sidebar-item active">
                <Icon name="BarChart3" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Supervisor-Dashboard</span>
              </div>
              <div className="sidebar-item" onClick={() => navigate('/system-administration-panel')}>
                <Icon name="Settings" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Systemverwaltung</span>
              </div>
              <div className="sidebar-item" onClick={() => navigate('/audit-trail-and-compliance-viewer')}>
                <Icon name="Shield" size={20} className="sidebar-item-icon" />
                <span className="sidebar-item-text">Audit & Compliance</span>
              </div>
            </div>
          </nav>
        </aside>
        {/* Header */}
        <header className={`header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="header-content">
            <div className="header-left">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-md hover:bg-muted transition-colors">

                <Icon name="Menu" size={20} />
              </button>
              <div className="breadcrumb">
                <span className="breadcrumb-item">
                  <span className="breadcrumb-link" onClick={() => navigate('/case-worker-dashboard')}>Dashboard</span>
                </span>
                <Icon name="ChevronRight" size={16} className="breadcrumb-separator" />
                <span className="breadcrumb-current">Supervisor-Dashboard</span>
              </div>
            </div>
            <div className="header-right">
              <div className="session-timer">
                <Icon name="Clock" size={16} />
                <span>Session: 2:45:30</span>
              </div>
              <div className="notification-button">
                <Icon name="Bell" size={20} />
                <div className="notification-badge" />
              </div>
              <div className="header-user">
                <div className="header-user-avatar">MS</div>
                <div className="header-user-info">
                  <div className="header-user-name">Max Supervisor</div>
                  <div className="header-user-role">Abteilungsleiter</div>
                </div>
              </div>
            </div>
          </div>
        </header>
        {/* Main Content */}
        <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">Supervisor-Dashboard & Analytics</h1>
                <p className="text-muted-foreground">Umfassende Teamübersicht und Leistungsanalyse</p>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  options={timeRangeOptions}
                  value={selectedTimeRange}
                  onChange={setSelectedTimeRange}
                  className="w-48" />

                <Select
                  options={departmentOptions}
                  value={selectedDepartment}
                  onChange={setSelectedDepartment}
                  className="w-56" />

                <Button variant="default" iconName="Download" iconPosition="left" onClick={handleExportReport}>
                  Bericht exportieren
                </Button>
              </div>
            </div>

            {/* Active Alerts */}
            {activeAlerts?.length > 0 &&
            <div className="space-y-3">
                {activeAlerts?.map((alert) =>
              <AlertNotification
                key={alert?.id}
                alert={alert}
                onDismiss={handleDismissAlert}
                onInvestigate={handleInvestigateAlert} />

              )}
              </div>
            }

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kpiData?.map((kpi) =>
              <KPICard key={kpi?.id} {...kpi} />
              )}
            </div>

            {/* Main Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Panel - Team Members */}
              <div className="lg:col-span-3 space-y-4">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Team-Mitglieder</h3>
                    <span className="text-sm text-muted-foreground">{teamMembers?.filter((m) => m?.status === 'Aktiv')?.length} aktiv</span>
                  </div>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-thin">
                    {teamMembers?.map((member) =>
                    <TeamMemberCard
                      key={member?.id}
                      member={member}
                      onViewDetails={handleViewMemberDetails} />

                    )}
                  </div>
                </div>
              </div>

              {/* Center Panel - Analytics */}
              <div className="lg:col-span-6 space-y-6">
                <PerformanceChart
                  type="bar"
                  data={performanceData}
                  title="Wöchentliche Bearbeitungen"
                  dataKey="bearbeitungen"
                  xAxisKey="name" />

                <PerformanceChart
                  type="line"
                  data={productivityData}
                  title="Team-Produktivität (4 Wochen)"
                  dataKey="produktivität"
                  xAxisKey="name" />

                <QuickActionPanel onAction={handleQuickAction} />
              </div>

              {/* Right Panel - Approval Queue & System Health */}
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Genehmigungswarteschlange</h3>
                    <span className="text-sm font-medium text-error">{approvalQueue?.length} ausstehend</span>
                  </div>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin">
                    {approvalQueue?.map((item) =>
                    <ApprovalQueueItem
                      key={item?.id}
                      item={item}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onViewDetails={handleViewDetails} />

                    )}
                  </div>
                </div>

                <SystemHealthPanel systems={systemHealth} />
              </div>
            </div>

            {/* Footer Info */}
            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Letzte Aktualisierung: {new Date()?.toLocaleString('de-DE')}</span>
                <span>© {new Date()?.getFullYear()} Bundesagentur für Arbeit</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default SupervisorDashboardAndAnalytics;